import { useCallback, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { CloudUpload } from 'lucide-react'
import { Button } from '@neo/primitives/button'
import { useFileUpload } from 'v2/shared/hooks/use-file-upload'
import { FileMimeType, type RFileUploadResponse } from 'v2/types/api/auth'
import { Label } from '@neo/primitives/label'
import { Input } from '@neo/primitives/input'
import { v4 } from 'uuid'
import { errorToast } from 'v2/utils/toasts'
import { ACCEPTED_FILE_TYPES, getFileType } from '../../utils/media-upload'
import { MediaPreview } from './media-preview'
import { NodevarList } from '../nodevar-list'

interface MediaUploadProps {
  onURLChange: (uploadedFile: RFileUploadResponse) => void
  label: string
  initialFiles: RFileUploadResponse[]
  multiUpload?: boolean
  nodeVars?: string[]
  onRemove: (fileToRemove: RFileUploadResponse) => void
  disableFiles?: string[]
}

export const MediaUpload = ({
  onURLChange,
  onRemove,
  label,
  initialFiles,
  multiUpload = true,
  nodeVars,
  disableFiles = [],
}: MediaUploadProps) => {
  const { uploadFile, isUploading, removeFile } = useFileUpload((file) => {
    onURLChange(file)
  })

  const fileType = disableFiles.length
    ? Object.fromEntries(
        Object.entries(ACCEPTED_FILE_TYPES).filter(
          ([key]) => !disableFiles.some((type) => key.startsWith(`${type}/`)),
        ),
      )
    : ACCEPTED_FILE_TYPES

  const getUniqueFiles = useCallback(
    (files: RFileUploadResponse[]) => [
      ...new Map(files.map((file) => [file.file_url, file])).values(),
    ],
    [],
  )

  const handleFiles = useCallback(
    async (acceptedFiles: File[]) => {
      await Promise.all(
        acceptedFiles.map(async (file) => {
          const type = getFileType(file)
          if (type) uploadFile(file)
        }),
      )
    },
    [uploadFile],
  )

  const handleRemoveFile = useCallback(
    (fileToRemove: RFileUploadResponse) => {
      removeFile(fileToRemove.file_id)
      onRemove(fileToRemove)
    },
    [onRemove, removeFile],
  )

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: fileDialogOpen,
    fileRejections,
  } = useDropzone({
    onDrop: handleFiles,
    accept: fileType,
    maxFiles: multiUpload ? 10 - initialFiles.length : 1,
    noClick: true,
    noKeyboard: true,
    multiple: multiUpload,
  })

  const isUploadDisabled =
    isUploading ||
    (multiUpload && initialFiles.length >= 10) ||
    (!multiUpload && initialFiles.length >= 1)

  useEffect(() => {
    if (fileRejections.length) {
      errorToast({
        title: 'Error',
        description: 'Failed to upload files, max 10 files allowed',
      })
    }
  }, [fileRejections])

  return (
    <div className="w-full max-w-2xl mx-auto">
      <Label>{label}</Label>
      <div className="flex gap-2 flex-col items-center mt-1">
        <div className="w-full" {...getRootProps()}>
          <input {...getInputProps()} />
          <Button
            className="w-full p-4 flex gap-4 h-auto text-left text-tertiary justify-start"
            type="button"
            onClick={fileDialogOpen}
            variant="outline"
            disabled={isUploadDisabled}
            data-testid="button-upload-media"
          >
            <CloudUpload />
            {isUploading ? (
              <div>Uploading...</div>
            ) : (
              <div>
                <div className="text-xs-600">
                  {isDragActive ? 'Drop the files here' : 'Drag files or click to upload'}
                </div>
                <div className="text-xs-400">Supported files: Image, Audio, Video & Documents</div>
              </div>
            )}
          </Button>
        </div>
        <div className="w-full flex flex-col gap-2 items-center">
          <div className="text-s-500 text-tertiary">or</div>
          <Input
            type="text"
            placeholder="Select a variable"
            value={initialFiles
              .filter((file) => !file.file_name)
              .map((file) => file.file_url)
              .join(', ')}
            className="w-full"
            disabled
            onChange={(e) => {
              const matches = e.target.value.match(/\{\{[^}]+\}\}/g) || []
              const lastVar = matches[matches.length - 1] || ''
              onURLChange({
                file_url: lastVar,
                file_name: '',
                file_size: 0,
                file_type: FileMimeType.image_jpeg,
                file_id: v4(),
              })
            }}
            id="media-upload-variable-name"
            endIcon={
              <NodevarList
                nodevars={nodeVars}
                className="pr-2"
                inputElementSelector="#media-upload-variable-name"
              />
            }
          />
        </div>
      </div>

      {[...initialFiles].length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 gap-4">
          <MediaPreview files={getUniqueFiles([...initialFiles])} onRemove={handleRemoveFile} />
        </div>
      )}
    </div>
  )
}
